import { applyServiceFee, centsToDollars } from "@outschool/business-rules";
import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import { PaymentScheduleTypes } from "@outschool/gql-backend-generated";
import {
  AvailableSubscriptionPackagesQuery,
  AvailableSubscriptionPackagesQueryVariables,
  ChooseAPlanEnrollmentsQueryQuery,
  ChooseAPlanEnrollmentsQueryQueryVariables,
  UpdateCurrentUserDetailsMutation,
  UpdateCurrentUserDetailsMutationVariables,
} from "@outschool/gql-frontend-generated";
import { dayjs } from "@outschool/time";
import {
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import {
  FullCurrentUserFragment,
  useFundingProgramAbbr,
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React from "react";

const updateCurrentUserDetailsMutation = gql`
  mutation UpdateCurrentUserDetails($details: UserDetailsInput) {
    updateCurrentUser(details: $details) {
      ...FullCurrentUserFragment
    }
  }
  ${FullCurrentUserFragment}
`;

let isCurrentUserDetailsUpdated = false;

export function useRequiresMembershipForEnrollment() {
  const { isLoggedIn, currentUser, isEsaSession } = useSession();
  const esaState = useFundingProgramAbbr();
  const { usesCharter } = useSelfElectedStateFundingProgram();

  const enableRequireEsaMembershipForEnrollment = useFeatureFlag(
    "require-esa-membership-for-enrollment"
  );

  const [updateCurrentUserDetails] = useMutation<
    UpdateCurrentUserDetailsMutation,
    UpdateCurrentUserDetailsMutationVariables
  >(updateCurrentUserDetailsMutation);

  const currentUserRequiresMembershipForEnrollment =
    !!currentUser?.requiresMembershipForEnrollment;

  // exclude admins from the requirement for now
  const isAdmin = !!currentUser?.is_admin;

  const requiresMembershipForEnrollment =
    ((!isLoggedIn && !usesCharter()) ||
      currentUserRequiresMembershipForEnrollment ||
      // avoid briefly showing cash for i18n users and required membership states
      currentUser?.requiresMembershipForEnrollment === null) &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership &&
    !isAdmin;

  React.useEffect(() => {
    if (
      currentUser?.requiresMembershipForEnrollment === null &&
      !isAdmin &&
      !isCurrentUserDetailsUpdated
    ) {
      // using a module-level variable to prevent multiple update requests
      isCurrentUserDetailsUpdated = true;
      updateCurrentUserDetails({
        variables: {
          details: {
            requiresMembershipForEnrollment: true,
            // Track phase of the membership rollout we are doing.
            membershipRolloutPhase: "phase_3_final_rollout",
          },
        },
      });
      // no need to clear isCurrentUserDetailsUpdated because we will only ever need to set once
    }
  }, [
    currentUser?.requiresMembershipForEnrollment,
    updateCurrentUserDetails,
    isAdmin,
  ]);

  const requiresEsaMembershipForEnrollment =
    enableRequireEsaMembershipForEnrollment &&
    isLoggedIn &&
    isEsaSession &&
    programsRequireCreditBundle.includes(esaState ?? "") &&
    !currentUser?.hasEsaMembership;

  const mustPurchaseMembership =
    requiresMembershipForEnrollment &&
    (!currentUser?.hasSubscription ||
      !currentUser?.canPayWithSubscriptionCredits);

  return {
    requiresMembershipForEnrollment,
    mustPurchaseMembership,
    requiresEsaMembershipForEnrollment,
  };
}

export function useIsEligibleFor40PercentCouponUpsell() {
  const { isLoggedIn, currentUser, isEsaSession } = useSession();

  const isExistingALaCarteBuyer = !!currentUser?.firstPaidEnrollment;

  // Audience for 40 percent coupon:
  // logged in users who have purchased a la carte classes, but do not have a membership (or any credits)
  // and are not ESA users or users in excluded states
  const isEligibleFor40PercentCouponUpsell =
    isLoggedIn &&
    isExistingALaCarteBuyer &&
    !currentUser?.hasSubscription &&
    !currentUser?.canPayWithSubscriptionCredits &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership;

  return {
    isEligibleFor40PercentCouponUpsell,
  };
}

export function useShowCredits() {
  const {
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment,
  } = useRequiresMembershipForEnrollment();
  const { currentUser } = useSession();
  return (
    requiresMembershipForEnrollment ||
    requiresEsaMembershipForEnrollment ||
    currentUser?.canPayWithSubscriptionCredits
  );
}

const SUBSCRIPTION_PACKAGES_QUERY = gql`
  query AvailableSubscriptionPackages {
    availableSubscriptionPackages {
      uid
      amountCents
      numCredits
      savingsPercentage
      savingsCents
      classPerWeekAvg
      numFreeCredits
    }
  }
`;

export function useSubscriptionPlans() {
  const { data, loading, error } = useQueryWithPreviousData<
    AvailableSubscriptionPackagesQuery,
    AvailableSubscriptionPackagesQueryVariables
  >(SUBSCRIPTION_PACKAGES_QUERY);
  return {
    subscriptionPlans: data?.availableSubscriptionPackages ?? [],
    loading,
    error,
  };
}

export const chooseAPlanEnrollmentsQuery = gql`
  query chooseAPlanEnrollmentsQuery($start: DateTime!, $end: DateTime!) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        uid
        price_cents
        ongoingStartTime
        service_fee_percentage
        section {
          uid
          activity {
            uid
            title
            hasTeacherSchedule
          }
          meetings {
            uid
            start_time
            end_time
            paidAt
          }
        }
        paymentSchedule {
          uid
          scheduleType
          amountCents
        }
      }
    }
  }
`;

type ActiveEnrollmentsType = NonNullable<
  NonNullable<
    ChooseAPlanEnrollmentsQueryQuery["currentUser"]
  >["activeEnrollments"]
>;

export function useApproximateMonthlySpendFromActiveEnrollments(skip: boolean) {
  const { start, end } = React.useMemo(() => {
    const now = dayjs();
    return {
      start: now.subtract(1, "month").toDate(),
      end: now.toDate(),
    };
  }, []);

  const { data: activeEnrollmentsInThePastMonthData } =
    useQueryWithPreviousData<
      ChooseAPlanEnrollmentsQueryQuery,
      ChooseAPlanEnrollmentsQueryQueryVariables
    >(chooseAPlanEnrollmentsQuery, {
      variables: { start, end },
      skip,
    });

  const activeEnrollmentsInThePastMonth: ActiveEnrollmentsType =
    activeEnrollmentsInThePastMonthData?.currentUser?.activeEnrollments ?? [];

  if (activeEnrollmentsInThePastMonth.length > 0) {
    const monthlySpend = Math.round(
      centsToDollars(
        activeEnrollmentsInThePastMonth.reduce((total, enrollment) => {
          if (enrollment && enrollment.paymentSchedule) {
            // Club enrollments
            if (
              enrollment.paymentSchedule &&
              enrollment.paymentSchedule.amountCents &&
              enrollment.paymentSchedule.scheduleType ===
                PaymentScheduleTypes.MonthlyFirst
            ) {
              total += applyServiceFee(
                enrollment.paymentSchedule.amountCents,
                enrollment.service_fee_percentage
              );
              // Weekly enrollments (1:1)
            } else if (
              enrollment &&
              enrollment.price_cents &&
              enrollment?.section?.activity.hasTeacherSchedule
            ) {
              const numMeetings = enrollment.section.meetings.filter(
                meeting =>
                  dayjs(meeting.start_time).isBefore(end) &&
                  dayjs(meeting.start_time).isAfter(start) &&
                  !!meeting.paidAt
              ).length;
              total += applyServiceFee(
                enrollment.price_cents * numMeetings,
                enrollment.service_fee_percentage
              );
              // Weekly enrollments (courses)
            } else if (
              enrollment.paymentSchedule &&
              enrollment.paymentSchedule.amountCents &&
              enrollment.paymentSchedule.scheduleType ===
                PaymentScheduleTypes.WeeklySunday
            ) {
              total += applyServiceFee(
                enrollment.paymentSchedule.amountCents * 4,
                enrollment.service_fee_percentage
              );
            }
            // Weekly enrollments (Ongoing)
          } else if (
            enrollment &&
            enrollment.price_cents &&
            enrollment.ongoingStartTime
          ) {
            total += applyServiceFee(
              enrollment.price_cents * 4,
              enrollment.service_fee_percentage
            );
            // upfront enrollments
          } else if (enrollment && enrollment.price_cents) {
            total += applyServiceFee(
              enrollment.price_cents,
              enrollment.service_fee_percentage
            );
          }
          return total;
        }, 0)
      )
    );
    return monthlySpend;
  } else {
    return 0;
  }
}
